/**
 * Press Releases Page (zh-hant)
 * /press-releases/
 * 
 * Displays a paginated list of press releases from Sanity CMS.
 * Data: allSanityRecord filtered by category "Press Releases"
 */
import React, { useState } from 'react'
import { graphql, PageProps } from 'gatsby'
import Layout from '../components/Layout'
import HeroBanner from '../components/HeroBanner'
import SEO from '../components/SEO'
import PageTitle from '../components/PageTitle'
import RecordList from '../components/RecordList'
import RecordItem from '../components/RecordItem'
import EmptyState from '../components/EmptyState'
import Pagination from '../components/Pagination'
import { IRDocumentConnection } from '../types'
import pressReleasesData from '../data/press-releases.json'

// ─── GraphQL Query ────────────────────────────────────────────────────────────

export const query = graphql`
  query PressReleasesQuery {
    records: allSanityRecord(
      filter: { category: { name: { en: { eq: "Press Releases" } } } }
      sort: { fields: date, order: DESC }
    ) {
      totalCount
      edges {
        node {
          id
          name {
            zhHant
            en
          }
          date
          file {
            zhHant {
              asset {
                url
              }
            }
            en {
              asset {
                url
              }
            }
          }
        }
      }
    }
  }
`

// ─── Component ────────────────────────────────────────────────────────────────

interface PressReleasesPageProps extends PageProps {
  data?: {
    records: IRDocumentConnection
  }
}

const PER_PAGE = 25
const PAGE_TITLE = '新聞發布'
const LANG_KEY = 'zh-hant' as const
const LANG_FIELD = 'zhHant' as const

const localRecords = pressReleasesData as IRDocumentConnection

const PressReleasesPage: React.FC<PressReleasesPageProps> = ({ data, location }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const records = data?.records?.totalCount ? data.records : localRecords
  const { edges: recordEdges, totalCount } = records

  const endIdx = PER_PAGE * currentPage
  const startIdx = endIdx - PER_PAGE
  const isFirst = currentPage === 1
  const isLast = totalCount === 0 || currentPage === Math.ceil(totalCount / PER_PAGE)
  const currentItems = recordEdges.slice(startIdx, endIdx)

  return (
    <Layout langKey={LANG_KEY} fluid={true} currentPath={location.pathname}>
      <SEO title={PAGE_TITLE} lang="zh-TW" />
      <HeroBanner />
      <PageTitle title={PAGE_TITLE} />

      <RecordList>
        {currentItems.length > 0 ? (
          currentItems.map(({ node }, idx) =>
            node.name[LANG_FIELD] ? (
              <RecordItem
                key={node.id || `record-${idx}`}
                {...node}
                langKey={LANG_FIELD}
                isLast={totalCount === idx + 1}
              />
            ) : null
          )
        ) : (
          <EmptyState langKey={LANG_KEY} />
        )}
      </RecordList>

      <Pagination
        langKey={LANG_KEY}
        perPage={PER_PAGE}
        isFirst={isFirst}
        isLast={isLast}
        currentPage={currentPage}
        setCurrentPage={(page) => {
          setCurrentPage(page)
          window.scrollTo(0, 0)
        }}
        noRecord={totalCount === 0}
        totalCount={totalCount}
      />
    </Layout>
  )
}

export default PressReleasesPage
